import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Card, CardContent, Chip, Divider, Alert, Badge } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL, API_PATH, BASE_PATH } from './config';
import { useTranslation } from 'react-i18next';

const SharingPage = () => {
    const { t } = useTranslation();
    const [sharedWithMe, setSharedWithMe] = useState([]);
    const [sharedByMe, setSharedByMe] = useState([]);
    const [pendingShares, setPendingShares] = useState([]);
    const [myTrips, setMyTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [selectedTripId, setSelectedTripId] = useState('');
    const [shareWithUserId, setShareWithUserId] = useState('');

    const fetchPendingShares = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/pending_shares.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setPendingShares(data.pending_shares || []);
            }
        } catch (err) {
            console.error('Failed to fetch pending shares:', err);
        }
    }, []);

    const fetchSharedPlans = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/shared_plans.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSharedWithMe(data.shared_with_me || []);
                setSharedByMe(data.shared_by_me || []);
            } else {
                setError(t('fetch_shared_plans_error'));
            }
        } catch (err) {
            setError(t('fetch_shared_plans_general_error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const fetchMyTrips = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/get.php?status=active`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setMyTrips(data || []);
            }
        } catch (err) {
            console.error('Failed to fetch trips:', err);
        }
    }, []);

    useEffect(() => {
        fetchPendingShares();
        fetchSharedPlans();
        fetchMyTrips();
    }, [fetchPendingShares, fetchSharedPlans, fetchMyTrips]);

    const handleRespondToShare = async (sharedTripId, action) => {
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/respond_share.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    shared_trip_id: sharedTripId,
                    action: action
                })
            });

            const data = await response.json();
            
            if (response.ok) {
                setSuccess(data.message);
                fetchPendingShares();
                fetchSharedPlans();
            } else {
                setError(data.message || t('share_response_error'));
            }
        } catch (err) {
            setError(t('share_response_general_error'));
        }
    };

    const handleShareTrip = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!selectedTripId) {
            setError(t('select_trip_to_share'));
            return;
        }

        if (!shareWithUserId || shareWithUserId.length !== 5) {
            setError(t('enter_valid_user_id'));
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/share_trip.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    trip_id: selectedTripId,
                    shared_with_public_id: shareWithUserId.toUpperCase(),
                    permission_level: 'contributor'
                })
            });

            const data = await response.json();
            
            if (response.ok) {
                setSuccess(data.message || t('trip_shared_successfully'));
                setShareWithUserId('');
                setSelectedTripId('');
                fetchSharedPlans();
            } else {
                setError(data.message || t('share_plan_error'));
            }
        } catch (err) {
            setError(t('share_plan_general_error'));
        }
    };

    const handleArchiveSharedTrip = async (tripId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/archive.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ trip_id: tripId })
            });

            if (response.ok) {
                setSuccess(t('trip_archived_successfully') || 'Trip archived successfully');
                fetchSharedPlans(); // Refresh the shared trips list
            } else {
                setError(t('archive_trip_error'));
            }
        } catch (err) {
            setError(t('archive_trip_general_error'));
        }
    };

    if (loading) {
        return <Box sx={{ p: 2 }}><Typography>{t('loading_shared_plans')}</Typography></Box>;
    }

    return (
        <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" component="h2" gutterBottom>
                {t('sharing_title')}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Pending Share Requests */}
            {pendingShares.length > 0 && (
                <Card sx={{ mb: 4, border: '2px solid #ff9800', backgroundColor: '#fff8e1' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Badge badgeContent={pendingShares.length} color="warning">
                                📬
                            </Badge>
                            {t('pending_share_requests')}
                        </Typography>
                        
                        {pendingShares.map(share => (
                            <Card key={share.shared_trip_id} sx={{ mb: 2, backgroundColor: 'white' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {share.trip_name || `Trip ${share.trip_id}`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('shared_by')}: {share.owner_name || share.owner_public_id}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('permission_label')}: {t('full_access') || 'Full access'}
                                            </Typography>
                                            {share.start_date && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {share.start_date} - {share.end_date}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                startIcon={<CheckIcon />}
                                                onClick={() => handleRespondToShare(share.shared_trip_id, 'accept')}
                                            >
                                                {t('accept')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                startIcon={<CloseIcon />}
                                                onClick={() => handleRespondToShare(share.shared_trip_id, 'reject')}
                                            >
                                                {t('reject')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </CardContent>
                </Card>
            )}
            {/* Share a Trip Form */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('share_a_trip')}
                    </Typography>
                    
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                    
                    <Box component="form" onSubmit={handleShareTrip}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>{t('select_trip')}</InputLabel>
                            <Select
                                value={selectedTripId}
                                label={t('select_trip')}
                                onChange={(e) => setSelectedTripId(e.target.value)}
                            >
                                {myTrips.map(trip => (
                                    <MenuItem key={trip.trip_id} value={trip.trip_id}>
                                        {trip.name || `Trip ${trip.trip_id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <TextField
                            label={t('share_user_id_label')}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={shareWithUserId}
                            onChange={(e) => setShareWithUserId(e.target.value.toUpperCase().slice(0, 5))}
                            placeholder="XXXXX"
                            inputProps={{ maxLength: 5, style: { textTransform: 'uppercase', letterSpacing: '2px' } }}
                            helperText={t('enter_5_char_user_id')}
                        />
                        
                        <Button type="submit" variant="contained" sx={{ mt: 2 }} fullWidth>
                            {t('share_trip_btn')}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Trips Shared With Me */}
            <Typography variant="h6" gutterBottom>
                {t('trips_shared_with_me')}
            </Typography>
            {sharedWithMe.length > 0 ? (
                <Box sx={{ mb: 4 }}>
                    {sharedWithMe.map(share => (
                        <Card key={share.shared_trip_id} sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {share.trip_name || `Trip ${share.trip_id}`}
                                    </Typography>
                                    <Chip 
                                        label={t('full_access') || 'Full access'} 
                                        color="primary"
                                        size="small"
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {t('shared_by')}: {share.owner_name || share.owner_public_id}
                                </Typography>
                                {share.start_date && (
                                    <Typography variant="body2" color="text.secondary">
                                        {share.start_date} - {share.end_date}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        href={`${BASE_PATH}/trip/${share.trip_id}`}
                                    >
                                        {t('view_trip')}
                                    </Button>
                                    <Button 
                                        variant="outlined" 
                                        color="warning"
                                        size="small" 
                                        onClick={() => handleArchiveSharedTrip(share.trip_id)}
                                    >
                                        {t('archive_trip_btn')}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : (
                <Typography color="text.secondary" sx={{ mb: 4 }}>{t('no_trips_shared_with_you')}</Typography>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Trips I've Shared */}
            <Typography variant="h6" gutterBottom>
                {t('trips_i_shared')}
            </Typography>
            {sharedByMe.length > 0 ? (
                <Box>
                    {sharedByMe.map(share => (
                        <Card key={share.shared_trip_id} sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {share.trip_name || `Trip ${share.trip_id}`}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label={share.status === 'pending' ? t('share_status_pending') : 
                                                   share.status === 'accepted' ? t('share_status_accepted') : 
                                                   share.status === 'rejected' ? t('share_status_rejected') : share.status}
                                            color={share.status === 'accepted' ? 'success' : 
                                                   share.status === 'rejected' ? 'error' : 'warning'}
                                            size="small"
                                        />
                                        <Chip 
                                            label={t('full_access') || 'Full access'} 
                                            color="primary"
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {t('shared_with')}: {share.shared_with_name || share.shared_with_public_id}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : (
                <Typography color="text.secondary">{t('no_trips_shared_by_you')}</Typography>
            )}}
        </Box>
    );
};

export default SharingPage;